import {
  computed, onBeforeUnmount, onMounted, ref,
  Ref,
} from 'vue';

import { MaybeNull } from '@/shared/types';
import { getSafeRef } from '@/shared/utils/template-refs.ts';

interface UseOverflowTransparencyProps {
  ref: Ref<MaybeNull<HTMLElement>>;
  direction: OverflowDirection;
}

export enum OverflowDirection {
  Horizontal= 'horizontal',
  Vertical = 'vertical',
}

export const useOverflowTransparency = (props: UseOverflowTransparencyProps) => {
  const contentScrollTopLength = ref(0);
  const contentScrollLeftLength = ref(0);
  const contentScrollBottomLength = ref(0);
  const contentScrollRightLength = ref(0);

  const getOverlayLength = (scrollLength: number, maxOverlayLength: number, maxScrollLengthPx: number) => {
    const length = (scrollLength * maxOverlayLength) / maxScrollLengthPx;

    if (length > maxOverlayLength) {
      return maxOverlayLength;
    }

    return length;
  };

  const contentOverlayTopHeight = computed(() => {
    const maxOverlayLength = 20;
    const maxScrollLengthPx = 50;

    if (contentScrollTopLength.value >= 0) {
      return getOverlayLength(contentScrollTopLength.value, maxOverlayLength, maxScrollLengthPx);
    }

    return 0;
  });

  const contentOverlayLeftWidth = computed(() => {
    if (!props.ref.value) {
      return 0;
    }

    const maxOverlayLength = 10;
    const maxScrollLengthPx = 25;

    if (contentScrollTopLength.value >= 0) {
      return getOverlayLength(contentScrollTopLength.value, maxOverlayLength, maxScrollLengthPx);
    }

    return 0;
  });

  const contentOverlayRightWidth = computed(() => {
    if (!props.ref.value) {
      return 0;
    }

    const maxOverlayLength = 10;
    const maxScrollLengthPx = 25;

    if (contentScrollRightLength.value > 0) { // 50px
      return getOverlayLength(contentScrollRightLength.value, maxOverlayLength, maxScrollLengthPx);
    }

    return 0;
  });

  const contentOverlayBottomHeight = computed(() => {
    const maxOverlayLength = 20;
    const maxScrollLengthPx = 50;

    if (contentScrollBottomLength.value > 0) {
      return getOverlayLength(contentScrollBottomLength.value, maxOverlayLength, maxScrollLengthPx);
    }

    return 0;
  });

  const getScrollTopLength = (el: HTMLElement) => {
    return el.scrollTop;
  };

  const getScrollBottomLength = (el: HTMLElement) => {
    return el.scrollHeight - el.clientHeight - el.scrollTop;
  };

  const getScrollLeftLength = (el: HTMLElement) => {
    return el.scrollLeft;
  };

  const getScrollRightLength = (el: HTMLElement) => {
    return el.scrollWidth - el.clientWidth - el.scrollLeft;
  };

  const updateScrollLength = () => {
    contentScrollTopLength.value = getScrollTopLength(getSafeRef(props.ref));
    contentScrollBottomLength.value = getScrollBottomLength(getSafeRef(props.ref));
    contentScrollLeftLength.value = getScrollLeftLength(getSafeRef(props.ref));
    contentScrollRightLength.value = getScrollRightLength(getSafeRef(props.ref));
  };

  const applyOverflowTransparency = () => {
    updateScrollLength();
  };

  onMounted(() => {
    getSafeRef(props.ref).addEventListener('scroll', updateScrollLength);

    // create initial value
    applyOverflowTransparency();
  });

  onBeforeUnmount(() => {
    getSafeRef(props.ref).removeEventListener('scroll', updateScrollLength);
  });

  const maskImage = computed(() => {
    if (props.direction === OverflowDirection.Horizontal) {
      return `linear-gradient(
        to right,
        transparent 0%,
        white ${contentOverlayLeftWidth.value}%,
        white ${100 - contentOverlayRightWidth.value}%, 
        transparent 100%
      )`;
    }

    if (props.direction === OverflowDirection.Vertical) {
      return `linear-gradient(
        to bottom,
        transparent 0%,
        white ${contentOverlayTopHeight.value}%,
        white ${100 - contentOverlayBottomHeight.value}%,
        transparent 100%
      )`;
    }

    return '';
  });

  return {
    // mask-image: v-bind(maskImage)
    maskImage,

    // use this to manually apply transparency
    applyOverflowTransparency,
  };
};
